
import { getJobs } from '@/api';
import JobTable from '@/components/dashBoard/jobTable/JobTable.vue';
import FiltersBar from '@/components/dashBoard/filtersBar/FiltersBar.vue';
import ReloadButton from '@/components/dashBoard/reloadButton/ReloadButton.vue';
import RequestForChangeIndicator from '@/components/dashBoard/requestForChangeIndicator/RequestForChangeIndicator.vue';
import SelectFields from '@/components/dashBoard/selectsFields/SelectFields.vue';
import StatusButtons from '@/components/dashBoard/statusButtons/StatusButtons.vue';
import Notification from '@/components/UI/Notification.vue';
import JobCompliance from '@/views/JobBooking/JobCompliance.vue';
import store from '@/store';
import { IGetJobs, IJobs, Job, User } from '@/models/main';
import { format, utcToZonedTime } from 'date-fns-tz';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Action } from 'vuex-class';
import { timeZoneDefault } from '@/utils/PortalConstants';
import * as routeNames from '@/router/routeNames';

import CounterWithIcon from '@/components/UI/CounterWithIcon.vue';

@Component({
  components: {
    CounterWithIcon,
    FiltersBar,
    JobCompliance,
    JobTable,
    StatusButtons,
    ReloadButton,
    RequestForChangeIndicator,
    SelectFields,
    Notification,
  },
  computed: {
    ...mapState([
      'jobData',
      'availableStates',
      'alertTimeout',
      'storeSearch',
      'storeShowCategory',
      'storeSelectedStates',
      'storeDrivers',
      'storeJobsWithRequest4Change',
    ]),
    ...mapState('moduleUser', { userSelectedDates: 'userSelectedDates' }),
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class Dashboard extends Vue {
  public loading = false;
  private timeout: number;
  private interval: number;
  private user!: User;
  private today: string = format(new Date(), 'yyyy-MM-dd');

  private storeSearch!: string;
  private storeSelectedStates!: string[];
  private alertTimeout!: number;
  private storeDrivers!: Array<string | number>;
  public storeShowCategory!: string;
  private availableStates!: string[];
  private userSelectedDates!: string[];
  private jobData!: IJobs;
  private storeJobsWithRequest4Change: Job[];

  public showFiltersBar = false;
  public filters: string[] = [];
  public filtersAttributes: string[] = [];

  public jobsWithRequest4Change: number[] = [];

  // Compliance modal
  public showModal = false;
  public currentJobIdx = -1;
  private sortedJobs: Job[] = [];

  public currentJobChanged(idx: number): void {
    this.currentJobIdx = idx;
  }

  public closeModal(): void {
    this.showModal = false;
    this.currentJobIdx = -1;
    this.$forceUpdate();
  }

  public openInEditModal(unique_id: string): void {
    const jobs =
      this.sortedJobs.length > 0
        ? this.sortedJobs
        : this.filteredItems(this.storeShowCategory, this.filters, this.filtersAttributes);
    this.currentJobIdx = jobs.findIndex(x => x.unique_id === unique_id);
    this.showModal = true;
  }

  /**
   * Update the sorted jobs array when sorting changes in JobTable
   */
  public sortedItemsChanged(sortedItems: Job[]): void {
    if (sortedItems && sortedItems.length > 0) {
      this.sortedJobs = [...sortedItems];
      // If modal is open, update current job index to match the new sorting
      if (this.showModal && this.currentJobIdx >= 0) {
        const filteredItems = this.filteredItems(this.storeShowCategory, this.filters, this.filtersAttributes);
        const currentJob = filteredItems[this.currentJobIdx];
        if (currentJob) {
          const newIndex = sortedItems.findIndex(job => job.id === currentJob.id);
          if (newIndex !== -1) {
            this.currentJobIdx = newIndex;
          }
        }
      }
    }
  }

  // / Compliance modal

  @Action
  private setJobData!: (jobs: IGetJobs) => void;

  public rfcCounterClicked() {
    if (this.jobsWithRequest4Change.length >= 1) {
      const query = { job_id: this.jobsWithRequest4Change.toString() };
      this.$router.push({ path: 'search', query: { ...query } }).catch(() => false);
    }
  }

  public notificationClick(props): void {
    this.dataSync();
    props.close();
  }

  private timeoutAlert(): void {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.$notify({
        group: 'data-outdated',
        title: 'The data is outdated',
        text: 'Click refresh button, please',
        type: 'warn',
      });
    }, 5 * 60 * 1000) as unknown as number; // 5min
  }

  public dataSync(): void {
    this.loading = true;
    this.jobsWithRequest4Change = [];
    // Check for user date
    if (!this.userSelectedDates[0]) {
      this.userSelectedDates[0] = this.today;
    }
    getJobs(this.userSelectedDates)
      .then((data: IGetJobs) => {
        this.setJobData(data);
        this.$notify({
          group: 'data-outdated',
          clean: true,
        });
        this.timeoutAlert();
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Server connection lost',
          text: 'Click refresh button, please',
          type: 'warn',
        });
      })
      .finally(() => {
        this.jobsWithRequest4Change = this.storeJobsWithRequest4Change.map(job => job.id);
        this.loading = false;
      });
  }

  public get showEdit(): boolean {
    return this.userSelectedDates.length === 1 && this.userSelectedDates[0] === this.today;
  }

  public toggleFiltersbar(): void {
    this.showFiltersBar = !this.showFiltersBar;
    if (!this.showFiltersBar) {
      this.filters = [];
      this.filtersAttributes = [];
    }
  }

  public updateFilters(filters: string[]): void {
    this.filters = filters;
  }

  public updateAttributeFilters(filters: string[]): void {
    this.filtersAttributes = filters;
  }

  public filteredItems(category: string, filters?: string[], filtersAttributes?: string[]): Job[] {
    let filteredJobs: Job[];
    // Filtered for category
    switch (category) {
      case 'all':
        filteredJobs = this.jobData.all;
        break;
      case 'unallocated':
        filteredJobs = this.jobData.unallocated;
        break;
      case 'progress':
        filteredJobs = this.jobData.in_progress;
        break;
      case 'completed':
        filteredJobs = this.jobData.completed;
        break;
      case 'cancelled':
        filteredJobs = this.jobData.cancelled;
        break;
      default:
        filteredJobs = [];
    }

    // Filtered for select state
    if (this.storeSelectedStates.length >= 1 && this.storeSelectedStates.length !== this.availableStates.length) {
      filteredJobs = filteredJobs.filter(
        (job: Job) =>
          (job.main_stop === 1 && this.storeSelectedStates.includes(job.suburb_state1)) ||
          (job.main_stop === 2 && this.storeSelectedStates.includes(job.suburb_state2)),
      );
    }

    // Filtered for search input
    if (this.storeSearch) {
      const search = this.storeSearch.toUpperCase();
      filteredJobs = filteredJobs.filter(
        (job: Job) =>
          job.id.toString().includes(search) ||
          job.customer_code.toUpperCase().includes(search) ||
          job.customer_code.toUpperCase().includes(search) ||
          job.stop1_name.toUpperCase().includes(search) ||
          job.stop2_name.toUpperCase().includes(search) ||
          (job.main_stop === 1 && job.suburb_name1.toUpperCase().includes(search)) ||
          (job.main_stop === 1 && job.suburb_state1.toUpperCase().includes(search)) ||
          (job.main_stop === 1 && job.driver1.toString().includes(search)) ||
          (job.main_stop === 2 && job.suburb_name2.toUpperCase().includes(search)) ||
          (job.main_stop === 2 && job.suburb_state2.toUpperCase().includes(search)) ||
          (job.main_stop === 2 && job.driver2.toString().includes(search)) ||
          (job.is_done &&
            job.notes &&
            job.notes[job.notes.length - 1] &&
            job.notes[job.notes.length - 1].note.toUpperCase().includes(search)),
      );
    }

    // Filtered for select drivers
    if (this.storeDrivers.length > 0) {
      filteredJobs = filteredJobs.filter(
        (job: Job) => this.storeDrivers.includes(job.driver1) || this.storeDrivers.includes(job.driver2),
      );
    }

    //  FILTERS:
    // Sptit to base
    // If these filtering change, we need to change the split-to-base icon showing conditions in the same way
    // function "showSplitToBase" in JobTable.vue
    if (filters?.includes('split-to-base')) {
      filteredJobs = filteredJobs.filter(
        (job: Job) =>
          job.split_base &&
          job.split_base !== 0 &&
          (!job.driver2 || job.driver2 === 0 || job.driver2 === 'NOT ALLOCATED'),
      );
    }
    // Request for change
    if (filters?.includes('req4change')) {
      filteredJobs = filteredJobs.filter((job: Job) => job.is_rfc);
    }
    // CAN'T Schedule
    if (filters?.includes('cant-schedule')) {
      filteredJobs = filteredJobs.filter((job: Job) => !job.can_schedule);
    }
    // Edited
    if (filters?.includes('edited')) {
      filteredJobs = filteredJobs.filter((job: Job) => job.edited === 1);
    }
    // Delayed
    if (filters?.includes('delayed')) {
      filteredJobs = filteredJobs.filter((job: Job) => job.delay >= 1);
    }
    // Futile
    if (filters?.includes('futile')) {
      filteredJobs = filteredJobs.filter((job: Job) => job.is_futile);
    }
    // Remove Tech 1, 2 and 3 if ANY TECH clicked
    if (filtersAttributes?.includes('TECH')) {
      const techAttrs = ['TECH1', 'TECH2', 'TECH3'];
      techAttrs.map(attr => {
        const attrIdx = this.filtersAttributes.indexOf(attr);
        if (attrIdx >= 0) {
          this.filtersAttributes.splice(attrIdx, 1);
        }
      });
    }

    // ATTRIBUTES FILTERS:
    filtersAttributes?.map(filter => {
      // Filter Tech 1, 2 and 3 if ANY TECH clicked
      if (filter === 'TECH') {
        filteredJobs = filteredJobs.filter(
          (job: Job) =>
            job.attributes.indexOf('TECH1') >= 0 ||
            job.attributes.indexOf('TECH2') >= 0 ||
            job.attributes.indexOf('TECH3') >= 0,
        );
      } else {
        filteredJobs = filteredJobs.filter((job: Job) => job.attributes.indexOf(filter) >= 0);
      }
    });

    return filteredJobs;
  }

  private timeCycle(): void {
    // update interval 5 min
    this.interval = setInterval(() => {
      this.dataSync();
    }, 5 * 60 * 1000) as unknown as number;
  }

  @Watch('$route.path', { immediate: true })
  private changeRoute(): void {
    this.today = format(utcToZonedTime(new Date(), this.user.timezone || timeZoneDefault), 'yyyy-MM-dd');
    if (this.$route.path === '/dash') {
      if (
        this.jobData.all.length === 0 ||
        store.getters.prevUrl.name !== routeNames.DETAILED_QUERY_JOB ||
        store.getters.prevUrl.name !== routeNames.DETAILED_PARAM_JOB
      ) {
        this.dataSync();
      }
    }
    if (this.$route.path === '/dash-tv') {
      this.dataSync();
      this.timeCycle();
    } else {
      clearInterval(this.interval);
    }
  }

  private beforeDestroy(): void {
    clearTimeout(this.timeout);
    clearInterval(this.interval);
  }

  public changeDate(dates): void {
    store.dispatch('moduleUser/setUserDates', [dates]);
    this.dataSync();
  }

  public req4changeClick(): void {
    if (this.jobsWithRequest4Change.length >= 1) {
      const query = { job_id: this.jobsWithRequest4Change.toString() };
      this.$router.push({ path: 'search', query: { ...query } }).catch(() => false);
    }
  }
}
